import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/Atomic/Atoms/Icon'
import { Radio } from '@/Atomic/Atoms/Radio'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import { DropdownMenu } from '@/Atomic/Organisms/DropdownMenu'
import { Sublist, SublistItem } from '@/Atomic/Templates/Sublist'
import { useMediaQuery } from '@/hooks/useMediaQuery'

import './FilterList.scss'

/**
 * Synthetic id of the "Unsaved" row — the applied-but-unsaved advanced filter.
 * It is not a saved filter, so it has no real id.
 */
export const CURRENT_FILTER_ID = '__current_filter__'

export interface FilterItem {
  id: string
  label: string
  isFavorite?: boolean
  /** Whether the filter was created by the user (saved locally) vs from API */
  isUserCreated?: boolean
  /** Timestamp when the filter was last used */
  lastUsed?: number
}

export interface FilterListProps {
  filters: FilterItem[]
  selectedFilterId?: string
  onSelectFilter: (id: string) => void
  onSearchChange: (value: string) => void
  onEditFilter?: (filter: FilterItem) => void
  onDuplicateFilter?: (filter: FilterItem) => void
  onDeleteFilter?: (filter: FilterItem) => void
  onToggleFavorite?: (id: string) => void
  onAdvancedFilter?: () => void
  onEditCurrentFilter?: () => void
  onSaveCurrentFilter?: () => void
  onDeleteCurrentFilter?: () => void
  onClearAllFilters?: () => void
  className?: string
  onMobileMenuOpen?: (filter: FilterItem, menuItems: MenuItemProps[]) => void
}

export const FilterList: React.FC<FilterListProps> = ({
  filters,
  selectedFilterId,
  onSelectFilter,
  onSearchChange,
  onEditFilter,
  onDuplicateFilter,
  onDeleteFilter,
  onToggleFavorite,
  onAdvancedFilter,
  onEditCurrentFilter,
  onSaveCurrentFilter,
  onDeleteCurrentFilter,
  onClearAllFilters,
  className,
  onMobileMenuOpen,
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 766px)')

  const filterLabelIdPrefix = 'filter-label'

  // Handle keyboard navigation for filter selection
  const handleFilterKeyDown = (
    e: React.KeyboardEvent,
    filterId: string,
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelectFilter(filterId)
    }
  }

  const getMenuItems = (filter: FilterItem): MenuItemProps[] => [
    {
      label: t('common.edit'),
      onClick: () => onEditFilter?.(filter),
      icon: 'edit',
    },
    {
      label: t('common.duplicate'),
      onClick: () => onDuplicateFilter?.(filter),
      icon: 'copy',
    },
    {
      label: t('common.delete'),
      onClick: () => onDeleteFilter?.(filter),
      icon: 'bin',
      type: 'danger',
    },
  ]

  const hasCurrentFilter = filters.some((f) => f.id === CURRENT_FILTER_ID)
  const hasActiveFilter = selectedFilterId || hasCurrentFilter

  return (
    <>
      <div className={classNames('filter-list-container', className)}>
        <div className="filter-list-header">
          <span className="header-title">
            {onAdvancedFilter ? t('advancedSearch.title') : t('common.filters')}
          </span>
          {onClearAllFilters && hasActiveFilter && (
            <button
              className="clear-filter-button"
              onClick={onClearAllFilters}
              title={t('advancedSearch.clearFilter')}
            >
              {t('advancedSearch.clearFilter')}
            </button>
          )}
        </div>

        {/* Always available — it starts a *new* filter, so it stays visible even
            while an unsaved one exists (Edit on that row reopens it instead). */}
        {onAdvancedFilter && (
          <button
            className="filter-advanced-button"
            onClick={onAdvancedFilter}
            aria-label={t('advancedSearch.advancedFilter')}
          >
            <Icon icon="filter-horizontal" size="md" />
            <span>{t('advancedSearch.advancedFilter')}</span>
          </button>
        )}

        {hasCurrentFilter && (
          <div className="filter-list-filters">
            <div className="filter-list-section-title">
              {t('common.unsaved')}
            </div>

            <div className="filter-list-content">
              <Sublist>
                {filters
                  .filter((f) => f.id === CURRENT_FILTER_ID)
                  .map((filter) => {
                    const isCurrentFilter = true
                    // Current filter is selected when no saved filter is selected
                    const isCurrentFilterSelected = !selectedFilterId
                    const currentFilterMenuItems: MenuItemProps[] = [
                      {
                        label: t('common.edit'),
                        onClick: () => onEditCurrentFilter?.(),
                        icon: 'edit',
                      },
                      {
                        label: t('common.save'),
                        onClick: () => onSaveCurrentFilter?.(),
                        icon: 'disk',
                      },
                      {
                        label: t('common.delete'),
                        onClick: () => onDeleteCurrentFilter?.(),
                        icon: 'bin',
                        type: 'danger',
                      },
                    ]
                    const filterLabelId = `${filterLabelIdPrefix}-${filter.id}`

                    return (
                      <SublistItem key={filter.id} className="filter-list-item">
                        <div
                          className={classNames('filter-item-clickable', {
                            'is-current-filter': isCurrentFilter,
                          })}
                          onClick={() => onSelectFilter(filter.id)}
                          onKeyDown={(e) => handleFilterKeyDown(e, filter.id)}
                          tabIndex={0}
                          role="radio"
                          aria-checked={isCurrentFilterSelected}
                          aria-label={`${filter.label} (unsaved filter)`}
                        >
                          <div className="filter-radio-wrapper">
                            <Radio
                              name="filter-selection"
                              value={filter.id}
                              checked={isCurrentFilterSelected}
                              onChange={() => onSelectFilter(filter.id)}
                              className="filter-radio"
                              tabIndex={-1}
                            />
                          </div>
                          <div className="filter-info">
                            <span id={filterLabelId} className="filter-label">
                              {filter.label}
                            </span>
                            <Tooltip
                              anchorSelect={`#${filterLabelId}`}
                              content={filter.label}
                              size="xs"
                            />
                          </div>
                        </div>

                        <div
                          className="filter-actions"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.nativeEvent.stopImmediatePropagation()
                          }}
                        >
                          {isMobile ? (
                            <div className="o-dropdown-container">
                              <Button
                                icon="more-horizontal"
                                variant="icon"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  onMobileMenuOpen?.(
                                    filter,
                                    currentFilterMenuItems,
                                  )
                                }}
                                buttonClassName="o-menu-button"
                                aria-label={t('common.more')}
                              />
                            </div>
                          ) : (
                            <DropdownMenu
                              menuItems={currentFilterMenuItems}
                              boundarySelector=".filter-list-container"
                              usePortal
                            />
                          )}
                        </div>
                      </SublistItem>
                    )
                  })}
              </Sublist>
            </div>
          </div>
        )}

        <div className="filter-list-filters">
          {onAdvancedFilter && (
            <div className="filter-list-section-title">{t('common.saved')}</div>
          )}

          <div className="filter-list-search">
            <Search
              name="filter-search"
              placeholder={t('common.search')}
              onChange={(e) => onSearchChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="filter-list-content" role="radiogroup">
            <Sublist>
              {filters
                .filter((f) => f.id !== CURRENT_FILTER_ID)
                .map((filter) => {
                  const filterLabelId = `${filterLabelIdPrefix}-${filter.id}`

                  return (
                    <SublistItem key={filter.id} className="filter-list-item">
                      <div
                        className="filter-item-clickable"
                        onClick={() => onSelectFilter(filter.id)}
                        onKeyDown={(e) => handleFilterKeyDown(e, filter.id)}
                        tabIndex={0}
                        role="radio"
                        aria-checked={selectedFilterId === filter.id}
                        aria-label={`${filter.label}${filter.isUserCreated ? '' : ' (classic filter)'}`}
                      >
                        <div className="filter-radio-wrapper">
                          <Radio
                            name="filter-selection"
                            value={filter.id}
                            checked={selectedFilterId === filter.id}
                            onChange={() => onSelectFilter(filter.id)}
                            className="filter-radio"
                            tabIndex={-1}
                          />
                        </div>
                        <div className="filter-info">
                          <span id={filterLabelId} className="filter-label">
                            {filter.label}
                          </span>
                          <Tooltip
                            anchorSelect={`#${filterLabelId}`}
                            content={filter.label}
                            size="xs"
                          />
                          {onAdvancedFilter && !filter.isUserCreated && (
                            <span className="filter-badge">
                              {t('common.classic')}
                            </span>
                          )}
                          <div
                            className="filter-favorite-wrapper"
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleFavorite?.(filter.id)
                            }}
                          >
                            <Icon
                              icon={filter.isFavorite ? 'star' : 'star-outline'}
                              size="sm"
                              className={classNames('filter-favorite-icon', {
                                'is-favorite': filter.isFavorite,
                              })}
                            />
                          </div>
                        </div>
                      </div>

                      {onAdvancedFilter && (
                        <div
                          className="filter-actions"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.nativeEvent.stopImmediatePropagation()
                          }}
                        >
                          {filter.isUserCreated ? (
                            isMobile ? (
                              <div className="o-dropdown-container">
                                <Button
                                  icon="more-horizontal"
                                  variant="icon"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onMobileMenuOpen?.(
                                      filter,
                                      getMenuItems(filter),
                                    )
                                  }}
                                  buttonClassName="o-menu-button"
                                  aria-label={t('common.more')}
                                />
                              </div>
                            ) : (
                              <DropdownMenu
                                menuItems={getMenuItems(filter)}
                                boundarySelector=".filter-list-container"
                                usePortal
                              />
                            )
                          ) : (
                            <div id={`filter-info-${filter.id}`}>
                              <Icon
                                icon="info-circle"
                                size="md"
                                className="filter-info-icon"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </SublistItem>
                  )
                })}
            </Sublist>
          </div>
        </div>
      </div>
      <Tooltip
        anchorSelect="[id^='filter-info-']"
        place="bottom"
        className="filter-info-tooltip"
        size="xs"
      >
        {t(
          'advancedSearch.filterNotEditableHere',
          "This filter can't be edited here.\nEdit it in Classic CRM.",
        )}
      </Tooltip>
    </>
  )
}
